import {
  folderColorValue,
  FolderPath,
  folderPathAncestry,
  folderTrail,
  LibraryFolderEntry,
  parentFolderPath,
} from '@lib/library/folderPath';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import { FolderTreeRowData, LibraryFolderStore } from '@stores/Library/LibraryFolder';
import classNames from 'classnames';
import { ReactNode, useCallback, useState } from 'react';
import { FolderGlyph } from './FolderGlyph';
import css from './FolderTree.module.scss';

/**
 * Which folders of the tree are open. Starts with the way down to `reveal` open (and `reveal`
 * itself, so its subfolders show), everything else folded to the top level — so a library of
 * hundreds of folders starts as a short list around where the user is.
 */
export const useFolderTreeExpansion = (folder: LibraryFolderStore, reveal: FolderPath | null) => {
  const [expanded, setExpanded] = useState<ReadonlySet<FolderPath>>(
    () => new Set(reveal ? folderPathAncestry(reveal) : []),
  );

  const toggle = useCallback(
    (path: FolderPath) =>
      setExpanded((previous) => {
        const next = new Set(previous);
        if (next.has(path)) next.delete(path);
        else next.add(path);
        return next;
      }),
    [],
  );

  /** Opens every folder above `path`, so it is on screen — a folder just created in there. */
  const show = useCallback(
    (path: FolderPath) =>
      setExpanded(
        (previous) => new Set([...previous, ...folderPathAncestry(parentFolderPath(path))]),
      ),
    [],
  );

  /** Whether every folder above `path` is open. The root has no toggle, so it never folds. */
  const isVisible = (path: FolderPath) =>
    folderPathAncestry(parentFolderPath(path))
      .slice(1)
      .every((above) => expanded.has(above));

  return {
    isExpanded: (path: FolderPath) => expanded.has(path),
    hasChildren: (path: FolderPath) => folder.childFoldersOf(path).length > 0,
    toggle,
    show,
    isVisible,
  };
};

/** `text` with the first case-insensitive match of `needle` marked. */
export const HighlightedName = ({ text, needle }: { text: string; needle?: string }) => {
  const at = needle ? text.toLowerCase().indexOf(needle) : -1;
  if (!needle || at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className={css.match}>{text.slice(at, at + needle.length)}</mark>
      {text.slice(at + needle.length)}
    </>
  );
};

/**
 * One row of the folder tree: the indent and its guides, the fold toggle, and a main element the
 * caller renders — a link in the finder, a selectable button in the picker — built from
 * `FolderTreeLabel`. `depth` 0 is a row with no toggle slot: a search result, or the library
 * itself in the picker.
 */
export function FolderTreeRow({
  depth,
  color,
  expandable,
  expanded,
  onToggle,
  name,
  className,
  children,
}: {
  depth: number;
  color?: string;
  expandable: boolean;
  expanded: boolean;
  onToggle: () => void;
  /** For the toggle's accessible name. */
  name: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      role="treeitem"
      aria-expanded={expandable ? expanded : undefined}
      className={classNames(css.row, className)}
      style={{ '--depth': depth, '--folder-color': folderColorValue(color) } as React.CSSProperties}
    >
      {depth > 0 && (
        <span className={css.toggleSlot}>
          {expandable && (
            <button
              type="button"
              className={classNames(css.toggle, expanded && css.toggleOpen)}
              aria-label={expanded ? `Collapse ${name}` : `Expand ${name}`}
              onClick={onToggle}
            >
              <ChevronRightRounded fontSize="small" />
            </button>
          )}
        </span>
      )}
      {children}
    </div>
  );
}

/** What a row says: the folder's tile, its name, and whatever the surface adds after it. */
export function FolderTreeLabel({
  entry,
  icon,
  name,
  needle,
  subtitle,
  trailing,
}: {
  entry?: LibraryFolderEntry;
  /** In place of the folder glyph — the library's own icon for the picker's root row. */
  icon?: ReactNode;
  name: string;
  needle?: string;
  subtitle?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <>
      <span className={css.icon}>{icon ?? <FolderGlyph color={entry?.color} size={18} />}</span>
      <span className={css.rowText}>
        <span className={css.rowName}>
          <HighlightedName text={name} needle={needle} />
        </span>
        {subtitle && <span className={css.rowParent}>{subtitle}</span>}
      </span>
      {trailing}
    </>
  );
}

/** The folders whose name holds `needle` (lowercased), from anywhere in the library, tree order. */
export const searchFolders = (folder: LibraryFolderStore, needle: string) =>
  folder.folderTree.filter(
    (row): row is FolderTreeRowData & { entry: LibraryFolderEntry } =>
      row.entry !== undefined && row.entry.name.toLowerCase().includes(needle),
  );

/** Where a search result sits: the chain of folders above it, "Apple Keynotes › 2015". */
export const folderLocation = (folder: LibraryFolderStore, path: FolderPath) =>
  folderTrail(parentFolderPath(path), folder.library.name ?? 'Library');

export function FolderSearchField({
  value,
  onChange,
  onSubmitFirst,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Enter: act on the first result — open it in the finder, pick it in the picker. */
  onSubmitFirst: () => void;
}) {
  return (
    <label className={css.search}>
      <SearchRounded fontSize="small" />
      <input
        autoFocus
        value={value}
        placeholder="Search folders"
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          // Also keeps Enter from submitting the picker's form with whatever was picked before.
          event.preventDefault();
          onSubmitFirst();
        }}
        aria-label="Search folders"
      />
    </label>
  );
}

export { css as folderTreeCss };
